/**
 * OC Message Rendering Functions
 * Provides render functions for OC-specific message UI and actions
 */

import React, { useContext } from 'react';

import { toggleSources as toggleSourcesAction } from '../../../store/transcript/actions';
import { TranscriptContext } from '../../../store/transcript/store';
import { MessageType } from '../../Message/types/messages';
import SearchResultFeedbackToggle from '../components/Feedback/SearchResultFeedbackToggle';
import HorizontalSourceList from '../components/Sources/HorizontalSourceList';
import SourceToggleButton from '../components/Sources/SourceToggleButton';
import { useOcFabWidgetOptions } from '../contexts/OcFabWidgetOptionsContext';
import { useIsNarrow } from '../hooks/useIsNarrow';

/**
 * Render additional UI below a message (horizontal source list)
 * Shows when message has sources and they are expanded
 * Uses context to check if horizontal sources should be shown
 */
export const ocRenderAdditionalMessageUI = (message: MessageType): React.ReactNode => {
  return <OcMessageSources message={message} />;
};

/**
 * Internal component that uses contexts to render sources
 */
const OcMessageSources: React.FC<{ message: MessageType }> = ({ message }) => {
  const { state } = useContext(TranscriptContext);
  const { showHorizontalSources } = useOcFabWidgetOptions();

  // Don't render if horizontal sources are disabled
  if (!showHorizontalSources) {
    return null;
  }

  if (!message.messageReferences || message.messageReferences.length === 0) {
    return null;
  }

  const isExpanded = state.expandedSources?.has(message.messageId) ?? false;
  const ariaId = `message-${message.messageId}-sources`;

  return (
    <HorizontalSourceList
      messageId={message.messageId}
      sources={message.messageReferences}
      isExpanded={isExpanded}
      id={ariaId}
      role="region"
      aria-labelledby={`${ariaId}-toggle`}
    />
  );
};

/**
 * Render additional actions for a message (source toggle button and feedback toggle)
 * Returns a component that uses context to determine what to render
 */
export const ocRenderAdditionalActions = (message: MessageType): React.ReactNode => {
  return <OcMessageActions key={`actions-${message.messageId}`} message={message} />;
};

/**
 * Internal component that uses contexts to render message actions
 */
const OcMessageActions: React.FC<{ message: MessageType }> = ({ message }) => {
  const { showFeedbackButton, showHorizontalSources } = useOcFabWidgetOptions();
  const isNarrow = useIsNarrow();
  const actions: React.ReactNode[] = [];

  // Add feedback toggle (only on wide screens)
  if (showFeedbackButton && !isNarrow) {
    actions.push(
      <OcFeedbackToggle key={`feedback-toggle-${message.messageId}`} message={message} />,
    );
  }

  // Add source toggle if message has sources
  if (showHorizontalSources && message.messageReferences && message.messageReferences.length > 0) {
    actions.push(
      <OcSourceToggle key={`source-toggle-${message.messageId}`} message={message} />,
    );
  }

  return <>{actions}</>;
};

/**
 * Internal component that uses TranscriptContext to render the toggle button
 */
const OcSourceToggle: React.FC<{ message: MessageType }> = ({ message }) => {
  const { state, dispatch } = useContext(TranscriptContext);
  const isExpanded = state.expandedSources?.has(message.messageId) ?? false;
  const ariaId = `message-${message.messageId}-sources`;

  if (!message.messageReferences || message.messageReferences.length === 0) {
    return null;
  }

  const handleToggle = (messageId: string) => {
    dispatch(toggleSourcesAction(messageId));
  };

  return (
    <div style={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
      <SourceToggleButton
        messageId={message.messageId}
        sourceCount={message.messageReferences.length}
        isExpanded={isExpanded}
        onToggle={handleToggle}
        ariaControlsId={ariaId}
        className="oc-flex-grow-1 oc-justify-content-end"
      />
    </div>
  );
};

/**
 * Internal component that renders the feedback toggle button
 */
const OcFeedbackToggle: React.FC<{ message: MessageType }> = ({ message }) => {
  return <SearchResultFeedbackToggle messageId={message.messageId} />;
};

